import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronRight, FileText, Folder, FolderOpen, Loader2, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { fmtDate, openGedDoc } from '../paiementsCb/gedUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ged-dossiers`;

const Detail = ({ dossierId, headers }) => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/${encodeURIComponent(dossierId)}`, { headers }).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [dossierId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Loader2 className="h-4 w-4 animate-spin text-slate-400 m-3" />;
  if (d.error) return <p className="text-xs text-red-600 p-3">Chargement impossible</p>;
  return (
    <div className="grid md:grid-cols-5 gap-2 p-3 bg-slate-50 rounded-b-xl" data-testid={`ged-dossier-detail-${dossierId}`}>
      {Object.entries(d.sous_dossiers).map(([nom, docs]) => (
        <div key={nom} className="rounded-lg border border-slate-200 bg-white p-2" data-testid={`ged-sous-dossier-${dossierId}-${nom}`}>
          <p className="text-xs font-semibold text-slate-700 flex items-center gap-1"><Folder className="h-3.5 w-3.5 text-amber-500" /> {nom} ({docs.length})</p>
          <ul className="mt-1 space-y-1">
            {docs.map((x) => (
              <li key={x.ged_id}>
                <button type="button" onClick={() => openGedDoc(x.ged_id, headers)} className="text-left text-[11px] text-sky-800 hover:underline flex gap-1" data-testid={`ged-doc-${x.ged_id}`}>
                  <FileText className="h-3 w-3 mt-0.5 shrink-0" /> <span>{x.doc_type_label} <span className="text-slate-400">· {fmtDate(x.created_at)}</span></span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default function GEDDossiers() {
  const { getAuthHeader } = useAuth();
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    axios.get(API, { headers: getAuthHeader(), params: { q } }).then((r) => setData(r.data)).catch(() => setData({ items: [], error: true }));
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const reprise = async () => {
    await axios.post(`${API}/reprise`, {}, { headers: getAuthHeader() });
    toast.success('Reprise de l\'existant lancée — actualisez dans quelques minutes');
  };
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="ged-dossiers">
      <div className="flex flex-wrap items-center gap-2">
        <FolderOpen className="h-5 w-5 text-amber-500" />
        <h3 className="font-bold text-slate-900">Dossiers membres & DOS-DOMIUM</h3>
        <span className="text-xs text-slate-500">classement automatique : Attestations · Reçus · Devis · Pièces · Relances</span>
        <div className="relative ml-auto">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un dossier" className="h-8 w-56 pl-8 text-sm" data-testid="ged-dossiers-search" />
        </div>
        <Button size="sm" variant="outline" onClick={reprise} className="h-8 gap-1 text-xs" data-testid="ged-reprise-btn"><RefreshCw className="h-3.5 w-3.5" /> Reprendre l'existant</Button>
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.items.length === 0
        ? <p className="text-sm text-slate-500 py-4 text-center" data-testid="ged-dossiers-empty">Aucun dossier classé pour l'instant.</p>
        : (
          <ul className="space-y-1.5" data-testid="ged-dossiers-liste">
            {data.items.map((d) => (
              <li key={d.dossier_id} className="rounded-xl border border-slate-200">
                <button type="button" onClick={() => setOpen(open === d.dossier_id ? null : d.dossier_id)} data-testid={`ged-dossier-${d.dossier_id}`}
                  className="w-full flex flex-wrap items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 rounded-xl">
                  <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${open === d.dossier_id ? 'rotate-90' : ''}`} />
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${d.type === 'domium' ? 'bg-violet-100 text-violet-800' : 'bg-sky-100 text-sky-800'}`}>{d.type === 'domium' ? 'DOS-DOMIUM' : 'Membre'}</span>
                  <span className="font-medium text-slate-900 text-sm">{d.label}</span>
                  <span className="text-xs text-slate-500 ml-auto">{Object.entries(d.sous_dossiers).filter(([, n]) => n).map(([k, n]) => `${k} ${n}`).join(' · ')}</span>
                </button>
                {open === d.dossier_id && <Detail dossierId={d.dossier_id} headers={getAuthHeader()} />}
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}
